import {
  Box,
  Divider,
  IconButton,
  Link,
  List,
  ListItem,
  Typography,
} from "@material-ui/core";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchOrders } from "redux/actions/orders";
import { DateTime } from "luxon";
import DateTimeUtils, { FORMAT_TYPE } from "utils/DateTimeUtils";
import { useParams } from "react-router-dom";
import { fetchOneGuestNote } from "redux/actions/guestnotes";
import OrderRes from "types/res/order/OrderRes";

const OrderHistory = () => {
  const dispatch = useDispatch();
  const companyId = useSelector((state) => state.account.cast.companyId);
  const castId = useSelector((state) => state.account.cast.castId);
  const { id } = useParams<any>();
  const guestNote = useSelector((state) => state.guestNotes)[0];

  // 月選択: デフォルトは当月
  const [selectedMonth, setSelectedMonth] = useState(
    DateTime.local().startOf("month")
  );

  const startDate = DateTimeUtils.toFormatAsLocalTimezone(
    selectedMonth.toJSDate(),
    FORMAT_TYPE.YEAR_DAY
  );
  const endDate = DateTimeUtils.toFormatAsLocalTimezone(
    selectedMonth.endOf("month").toJSDate(),
    FORMAT_TYPE.YEAR_DAY
  );

  const handlePrevMonth = () => {
    setSelectedMonth((prev) => prev.minus({ months: 1 }));
  };
  const handleNextMonth = () => {
    const next = selectedMonth.plus({ months: 1 });
    // 未来の月には進めない
    if (next <= DateTime.local().startOf("month")) {
      setSelectedMonth(next);
    }
  };
  const isCurrentMonth =
    selectedMonth.toFormat("yyyy-MM") ===
    DateTime.local().startOf("month").toFormat("yyyy-MM");

  useEffect(() => {
    if (!id) return;
    dispatch(
      fetchOneGuestNote({ companyId: companyId, guestNoteId: Number(id) })
    );
  }, [dispatch, id]);

  const orders = useSelector((state) => state.orders);
  const [filterOrders, setFilterOrders] = useState<OrderRes[]>([]);
  useEffect(() => {
    setFilterOrders(
      orders.filter((order) => order.guestId === guestNote?.guestId)
    );
  }, [guestNote, orders]);
  useEffect(() => {
    dispatch(fetchOrders({ companyId, castId, startDate, endDate }));
  }, [companyId, castId, startDate, endDate]);

  return (
    <>
      <Box padding={2}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="baseline"
        >
          <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>
            {filterOrders[0]?.guest?.name || "未設定"}様利用履歴
          </Typography>
          {filterOrders[0]?.guestId && (
            <Link href={`/guestNote/${guestNote?.guestNoteId}`}>
              <Typography style={{ fontWeight: "bold" }}>ノート</Typography>
            </Link>
          )}
        </Box>
      </Box>

      {/* 月選択ナビゲーション */}
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        style={{
          backgroundColor: "#f5f5f5",
          padding: "4px 0",
          borderTop: "1px solid #e0e0e0",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <IconButton onClick={handlePrevMonth} size="small">
          <ChevronLeftIcon />
        </IconButton>
        <Typography
          style={{
            fontWeight: "bold",
            fontSize: 16,
            minWidth: 120,
            textAlign: "center",
          }}
        >
          {selectedMonth.toFormat("yyyy年M月")}
        </Typography>
        <IconButton
          onClick={handleNextMonth}
          size="small"
          disabled={isCurrentMonth}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {filterOrders.length === 0 ? (
        <Box display="flex" justifyContent="center" padding={3}>
          <Typography color="textSecondary">
            この月の履歴はありません
          </Typography>
        </Box>
      ) : (
        <List>
          {filterOrders.map((order) => (
            <>
              <ListItem style={{ backgroundColor: "#eaeaea" }}>
                <Box
                  display="flex"
                  flex="1"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box display="flex" flexDirection="column" flexBasis="80%">
                    <Box display="flex" marginBottom={0.5}>
                      <Typography
                        style={{ width: "80px", fontWeight: "bold" }}
                      >
                        受付日
                      </Typography>
                      <Typography style={{ fontWeight: "bold" }}>
                        {order?.planInTime
                          ? DateTimeUtils.toFormatAsLocalTimezone(
                              order.planInTime,
                              FORMAT_TYPE.YEAR_DATE_TIME
                            )
                          : ""}
                      </Typography>
                    </Box>
                    <Box display="flex" marginBottom={0.5}>
                      <Typography
                        style={{ width: "80px", fontWeight: "bold" }}
                      >
                        店名
                      </Typography>
                      <Typography style={{ fontWeight: "bold" }}>
                        {order?.shop?.name || "未設定"}
                      </Typography>
                    </Box>
                    <Box display="flex" marginBottom={0.5}>
                      <Typography
                        style={{ width: "80px", fontWeight: "bold" }}
                      >
                        コース
                      </Typography>
                      <Typography style={{ fontWeight: "bold" }}>
                        {order?.course?.name || "未設定"}
                      </Typography>
                    </Box>
                    <Box display="flex" marginBottom={0.5}>
                      <Typography
                        style={{ width: "80px", fontWeight: "bold" }}
                      >
                        延長
                      </Typography>
                      <Typography style={{ fontWeight: "bold" }}>
                        {order?.additionalTime && order?.additionalTimeCount
                          ? `${
                              order?.additionalTime.time *
                              order.additionalTimeCount
                            }分`
                          : "未設定"}
                      </Typography>
                    </Box>
                    <Box display="flex" marginBottom={0.5}>
                      <Typography
                        style={{ width: "80px", fontWeight: "bold" }}
                      >
                        オプション
                      </Typography>
                      <Typography style={{ fontWeight: "bold" }}>
                        {order?.options
                          ?.map((option) => option?.name)
                          ?.join("/") || "未設定"}
                      </Typography>
                    </Box>
                    <Box display="flex" marginBottom={0.5}>
                      <Typography
                        style={{ width: "80px", fontWeight: "bold" }}
                      >
                        コスプレ
                      </Typography>
                      <Typography style={{ fontWeight: "bold" }}>
                        {order?.cosplay?.name || "未設定"}
                      </Typography>
                    </Box>
                    <Box display="flex" marginBottom={0.5}>
                      <Typography
                        style={{ width: "80px", fontWeight: "bold" }}
                      >
                        住所
                      </Typography>
                      <Typography style={{ fontWeight: "bold" }}>
                        {order?.orderAddress || order?.hotel?.name || "未設定"}
                      </Typography>
                    </Box>
                    <Box display="flex" marginBottom={0.5}>
                      <Typography
                        style={{ width: "80px", fontWeight: "bold" }}
                      >
                        売上
                      </Typography>
                      <Typography style={{ fontWeight: "bold" }}>
                        {order.totalFee}円
                      </Typography>
                    </Box>
                    <Box display="flex" marginBottom={0.5}>
                      <Typography
                        style={{ width: "80px", fontWeight: "bold" }}
                      >
                        支給
                      </Typography>
                      <Typography style={{ fontWeight: "bold" }}>
                        {order.totalCastFee}円
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </ListItem>
              <Divider />
            </>
          ))}
        </List>
      )}
    </>
  );
};

export default OrderHistory;
